import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import blogData from '../../data/blog.json';
import ImageUploader from './ImageUploader';

const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [isAddingPost, setIsAddingPost] = useState(false);
  const [newPost, setNewPost] = useState({
    id: null,
    title: '',
    slug: '',
    date: new Date().toISOString().split('T')[0],
    excerpt: '',
    content: '',
    image: 'https://picsum.photos/800/400',
    tags: []
  });
  const [newTag, setNewTag] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Common tags for quick selection
  const commonTags = ['React', 'JavaScript', 'Web Development', 'CSS', 'HTML', 'Frontend', 'Backend', 'Design', 'UX/UI'];

  useEffect(() => {
    // Load blog posts from the JSON file
    setPosts(blogData);
  }, []);

  const handleSavePosts = () => {
    // In a real implementation, this would save to the server
    setSuccessMessage('Blog posts saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    console.log('Posts to save:', posts);
    
    // You would typically use an API call here:
    // fetch('/api/posts', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(posts)
    // })
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleTitleChange = (e, isNewPost) => {
    const title = e.target.value;
    if (isNewPost) {
      setNewPost({
        ...newPost,
        title: title,
        slug: generateSlug(title)
      });
    } else {
      setEditingPost({
        ...editingPost,
        title: title,
        slug: generateSlug(title)
      });
    }
  };

  const handleAddPost = () => {
    if (!newPost.title || !newPost.content) return;
    
    const newId = Math.max(0, ...posts.map(p => p.id)) + 1;
    const postToAdd = { ...newPost, id: newId };
    setPosts([...posts, postToAdd]);
    setIsAddingPost(false);
    setNewPost({
      id: null,
      title: '',
      slug: '',
      date: new Date().toISOString().split('T')[0],
      excerpt: '',
      content: '',
      image: 'https://picsum.photos/800/400',
      tags: []
    });
  };

  const handleEditPost = (post) => {
    setEditingPost({ ...post });
  };

  const handleUpdatePost = () => {
    setPosts(posts.map(p => p.id === editingPost.id ? editingPost : p));
    setEditingPost(null);
  };

  const handleDeletePost = (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      setPosts(posts.filter(post => post.id !== id));
    }
  };

  const handleAddTag = (tag, isNewPost = false) => {
    if (isNewPost) {
      if (!newPost.tags.includes(tag)) {
        setNewPost({
          ...newPost,
          tags: [...newPost.tags, tag]
        });
      }
      setNewTag('');
    } else {
      if (!editingPost.tags.includes(tag)) {
        setEditingPost({
          ...editingPost,
          tags: [...editingPost.tags, tag]
        });
      }
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag, isNewPost = false) => {
    if (isNewPost) {
      setNewPost({
        ...newPost,
        tags: newPost.tags.filter(t => t !== tag)
      });
    } else {
      setEditingPost({
        ...editingPost,
        tags: editingPost.tags.filter(t => t !== tag)
      });
    }
  };

  const handleImageUpload = (url, isNewPost = false) => {
    if (isNewPost) {
      setNewPost({...newPost, image: url});
    } else {
      setEditingPost({...editingPost, image: url});
    }
  };

  return (
    <Container>
      <Header>
        <h2>Manage Blog Posts</h2>
        <AddButton onClick={() => setIsAddingPost(true)}>
          <FaPlus /> Add New Post
        </AddButton>
      </Header>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

      {isAddingPost && (
        <FormContainer>
          <h3>Add New Blog Post</h3>
          <FormRow>
            <FormGroup>
              <label>Title</label>
              <input
                type="text"
                value={newPost.title}
                onChange={(e) => handleTitleChange(e, true)}
                placeholder="Post Title"
              />
            </FormGroup>
            <FormGroup>
              <label>Slug (URL)</label>
              <input
                type="text"
                value={newPost.slug}
                onChange={(e) => setNewPost({...newPost, slug: e.target.value})}
                placeholder="post-url-slug"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <label>Date</label>
              <input
                type="date"
                value={newPost.date}
                onChange={(e) => setNewPost({...newPost, date: e.target.value})}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Excerpt</label>
            <textarea
              value={newPost.excerpt}
              onChange={(e) => setNewPost({...newPost, excerpt: e.target.value})}
              placeholder="A brief summary of the post"
              rows="2"
            />
          </FormGroup>

          <FormGroup>
            <label>Content (Markdown)</label>
            <textarea
              value={newPost.content}
              onChange={(e) => setNewPost({...newPost, content: e.target.value})}
              placeholder="Write your blog post content in Markdown format"
              rows="15"
            />
          </FormGroup>

          <FormGroup>
            <label>Featured Image</label>
            <PostImagePreview>
              {newPost.image && <img src={newPost.image} alt="Post preview" />}
            </PostImagePreview>
            <ImageUploader onImageUpload={(url) => handleImageUpload(url, true)} />
          </FormGroup>

          <FormGroup>
            <label>Tags</label>
            <TagsContainer>
              {newPost.tags.map((tag, index) => (
                <Tag key={index}>
                  {tag}
                  <RemoveTagButton onClick={() => handleRemoveTag(tag, true)}>×</RemoveTagButton>
                </Tag>
              ))}
            </TagsContainer>
            
            <TagInputContainer>
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add a tag"
              />
              <AddTagButton onClick={() => handleAddTag(newTag, true)}>Add</AddTagButton>
            </TagInputContainer>
            
            <CommonTagsContainer>
              <p>Quick tags:</p>
              {commonTags.map(tag => (
                <QuickTagButton 
                  key={tag} 
                  type="button" 
                  onClick={() => handleAddTag(tag, true)}
                  disabled={newPost.tags.includes(tag)}
                >
                  {tag}
                </QuickTagButton>
              ))}
            </CommonTagsContainer>
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handleAddPost}>Add Post</SaveButton>
            <CancelButton onClick={() => setIsAddingPost(false)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      {editingPost && (
        <FormContainer>
          <h3>Edit Blog Post</h3>
          <FormRow>
            <FormGroup>
              <label>Title</label>
              <input
                type="text"
                value={editingPost.title}
                onChange={(e) => handleTitleChange(e, false)}
                placeholder="Post Title"
              />
            </FormGroup>
            <FormGroup>
              <label>Slug (URL)</label>
              <input
                type="text"
                value={editingPost.slug}
                onChange={(e) => setEditingPost({...editingPost, slug: e.target.value})}
                placeholder="post-url-slug"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <label>Date</label>
              <input
                type="date"
                value={editingPost.date.substring(0, 10)}
                onChange={(e) => setEditingPost({...editingPost, date: e.target.value})}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Excerpt</label>
            <textarea
              value={editingPost.excerpt}
              onChange={(e) => setEditingPost({...editingPost, excerpt: e.target.value})}
              placeholder="A brief summary of the post"
              rows="2"
            />
          </FormGroup>

          <FormGroup>
            <label>Content (Markdown)</label>
            <textarea
              value={editingPost.content}
              onChange={(e) => setEditingPost({...editingPost, content: e.target.value})}
              placeholder="Write your blog post content in Markdown format"
              rows="15"
            />
          </FormGroup>

          <FormGroup>
            <label>Featured Image</label>
            <PostImagePreview>
              {editingPost.image && <img src={editingPost.image} alt="Post preview" />}
            </PostImagePreview>
            <ImageUploader onImageUpload={(url) => handleImageUpload(url)} />
          </FormGroup>

          <FormGroup>
            <label>Tags</label>
            <TagsContainer>
              {editingPost.tags.map((tag, index) => (
                <Tag key={index}>
                  {tag}
                  <RemoveTagButton onClick={() => handleRemoveTag(tag)}>×</RemoveTagButton>
                </Tag>
              ))}
            </TagsContainer>
            
            <TagInputContainer>
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add a tag"
              />
              <AddTagButton onClick={() => handleAddTag(newTag)}>Add</AddTagButton>
            </TagInputContainer>
            
            <CommonTagsContainer>
              <p>Quick tags:</p>
              {commonTags.map(tag => (
                <QuickTagButton 
                  key={tag} 
                  type="button" 
                  onClick={() => handleAddTag(tag)}
                  disabled={editingPost.tags.includes(tag)}
                >
                  {tag}
                </QuickTagButton>
              ))}
            </CommonTagsContainer>
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handleUpdatePost}>Update Post</SaveButton>
            <CancelButton onClick={() => setEditingPost(null)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      <PostsList>
        {posts.map(post => (
          <PostItem key={post.id}>
            <PostInfo>
              <PostImageThumb src={post.image} alt={post.title} />
              <PostDetails>
                <h3>{post.title}</h3>
                <PostDate>{new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</PostDate>
                <p>{post.excerpt}</p>
                <TagsList>
                  {post.tags.map((tag, index) => (
                    <small key={index}>#{tag}{index < post.tags.length - 1 ? ' ' : ''}</small>
                  ))}
                </TagsList>
              </PostDetails>
            </PostInfo>
            <PostActions>
              <EditButton onClick={() => handleEditPost(post)}>
                <FaEdit /> Edit
              </EditButton>
              <DeleteButton onClick={() => handleDeletePost(post.id)}>
                <FaTrash /> Delete
              </DeleteButton>
            </PostActions>
          </PostItem>
        ))}
      </PostsList>

      <SaveAllButton onClick={handleSavePosts}>Save All Changes</SaveAllButton>
    </Container>
  );
};

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(100, 255, 218, 0.1);
  color: var(--accent);
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
`;

const FormContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px;
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: var(--accent);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  
  ${FormGroup} {
    flex: 1;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const PostImagePreview = styled.div`
  width: 100%;
  height: 200px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  min-height: 40px;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: rgba(100, 255, 218, 0.1);
  color: var(--accent);
  border-radius: 20px;
  font-size: 0.9rem;
`;

const RemoveTagButton = styled.button`
  background: transparent;
  border: none;
  color: var(--accent);
  font-size: 1.2rem;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.8;
  }
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  
  input {
    flex: 1;
  }
`;

const AddTagButton = styled.button`
  padding: 10px 16px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CommonTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
`;

const QuickTagButton = styled.button`
  padding: 5px 10px;
  background-color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.disabled ? 'var(--text-secondary)' : 'var(--text-primary)'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: background-color 0.2s;
  
  &:not(:disabled):hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const PostsList = styled.div`
  margin-top: 30px;
`;

const PostItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PostInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const PostImageThumb = styled.img`
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 15px;
`;

const PostDetails = styled.div`
  flex: 1;
  
  h3 {
    margin: 0 0 5px;
    color: var(--text-primary);
    font-size: 1.1rem;
  }
  
  p {
    margin: 5px 0;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
`;

const PostDate = styled.div`
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  margin-bottom: 5px;
`;

const TagsList = styled.div`
  margin-top: 5px;
  
  small {
    color: var(--accent);
    font-size: 0.85rem;
  }
`;

const PostActions = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 107, 107, 0.1);
  }
`;

const SaveAllButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  margin-top: 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

export default BlogAdmin;